"use client";

import { ChangeEvent, ReactNode, useRef } from "react";
import IconUpload from "@/app/(components)/generics/Icons/IconUpload";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import defaultInputStyles from "@/app/(components)/generics/inputs/styles/DefaultInput.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import styles from "./FileInput.module.css";

type Props = {
  lang?: string;
  label?: string;
  labelSuffix?: ReactNode;
  required?: boolean;
  showRequiredLabel?: boolean;
  accept?: string;
  hint?: string;
  selectLabel: string;
  value: File | null;
  errorMessage?: string;
  disabled?: boolean;
  onChange: (file: File | null) => void;
};

export default function FileInput(props: Props) {
  const {
    lang,
    label,
    labelSuffix,
    required,
    showRequiredLabel,
    accept,
    hint,
    selectLabel,
    value,
    errorMessage,
    disabled,
    onChange,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.files?.[0] ?? null);
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={inputStyles.label}>
          {label}
          {showRequiredLabel &&
            (required ? (
              <span className={inputStyles.labelRequired}>*</span>
            ) : (
              <span className={inputStyles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
          {labelSuffix && <span className={inputStyles.labelSuffix}>{labelSuffix}</span>}
        </label>
      )}

      <button
        type="button"
        className={`${styles.dropzone} ${disabled ? styles.disabled : ""}`}
        onClick={() => !disabled && inputRef.current?.click()}
        disabled={disabled}
      >
        <span className={styles.icon}>
          <IconUpload />
        </span>
        <span className={styles.text}>
          <span className={styles.fileName}>{value ? value.name : selectLabel}</span>
          {hint && <span className={styles.hint}>{hint}</span>}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className={styles.nativeInput}
        disabled={disabled}
        onChange={handleChange}
      />

      {errorMessage && (
        <div className={defaultInputStyles.errorMessage}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
}
